/**
 * Utility for getting user-friendly labels for database fields
 * 
 * Uses WordPress field configuration to map database field names
 * to user-facing labels for display in charts and tables.
 */

import { underscoreToTitleCase } from "@utils/index";
import type { WpFieldConfig } from "@hooks/use-config/types";

/**
 * Get the display label for a field using WordPress config metadata.
 * Falls back to titlecasing the field name if config is unavailable.
 */
export function getFieldLabel(fieldName: string, wpConfig?: Record<string, WpFieldConfig>): string {
  if (!wpConfig?.[fieldName]) {
    return underscoreToTitleCase(fieldName);
  }
  return wpConfig[fieldName].name || underscoreToTitleCase(fieldName);
}

/**
 * Build a labelMap from WordPress field configuration options or taxonomy.
 * Converts { value: string, label: string }[] to { [value]: label }
 * or { slug: string, name: string }[] to { [slug]: name }
 * 
 * Note: Returns empty map for fields not in config (e.g., computed fields like "count")
 */
function buildLabelMapFromFieldOptions(
  fieldName: string,
  wpConfig?: Record<string, WpFieldConfig>
): Record<string | number, string> {
  const labelMap: Record<string | number, string> = {};
  
  // Silently return empty for fields not in config (like computed fields: count, sum, avg, etc.)
  if (!wpConfig?.[fieldName]) {
    return labelMap;
  }

  const field = wpConfig[fieldName];

  // Handle regular options: { value, label }[]
  if (field.options && Array.isArray(field.options)) {
    field.options.forEach((option: any) => {
      if (option && typeof option === 'object' && 'value' in option && 'label' in option) {
        labelMap[option.value] = option.label;
        // Also add string version for flexibility
        labelMap[String(option.value)] = option.label;
      }
    });
  }
  
  // Handle taxonomy: { slug, name }[]
  if (field.taxonomy && Array.isArray(field.taxonomy)) {
    field.taxonomy.forEach((taxValue: any) => {
      if (taxValue && typeof taxValue === 'object' && 'slug' in taxValue && 'name' in taxValue) {
        labelMap[taxValue.slug] = taxValue.name;
        // Also add string version for flexibility
        labelMap[String(taxValue.slug)] = taxValue.name;
      }
    });
  }
  
  // Return empty map for fields without options/taxonomy (computed fields like "count" or fields without label mappings)
  return labelMap;
}

/**
 * Build labelMaps for multiple field names from WordPress configuration.
 * Returns a merged record of all labelMaps.
 */
function buildLabelMapsForFields(
  fieldNames: string[],
  wpConfig?: Record<string, WpFieldConfig>
): Record<string | number, string> {
  const mergedLabelMap: Record<string | number, string> = {};
  
  fieldNames.forEach((fieldName) => {
    const fieldLabelMap = buildLabelMapFromFieldOptions(fieldName, wpConfig);
    Object.assign(mergedLabelMap, fieldLabelMap);
  });
  
  return mergedLabelMap;
}

/**
 * Build labelMaps for all dimensions in a chart from WordPress configuration.
 * Merges with any existing explicit labelMap from the chart config.
 */
export function enrichChartWithLabelMaps(
  chart: any,
  dimensions: string[],
  wpConfig?: Record<string, WpFieldConfig>
): any {
  if (!wpConfig || !dimensions) {
    return chart;
  }
  
  // Get labelMaps from field options
  const autoLabelMap = buildLabelMapsForFields(dimensions, wpConfig);
  
  // Merge with explicit labelMap (explicit takes precedence)
  const enrichedChart = {
    ...chart,
    labelMap: {
      ...autoLabelMap,
      ...(chart.labelMap || {}),
    },
  };

  return enrichedChart;
}
